import { colors } from '../../styles/theme';
import {
  CAD_PROJECT_PIN_IMAGE_PREFIX,
  CAD_PROJECT_PIN_STYLE,
} from '../../Map/styles/mapTheme';
import {
  CAD_PROJECT_PIN_KIND_CUSTOM,
  CAD_PROJECT_PIN_KIND_IDS,
  resolveCadProjectPinKind,
} from '../data/cadProjectPinKinds';
import {
  cadProjectPinImageId,
  getCadProjectPinKindColor,
} from './cadProjectPins';

/**
 * Paths Material Outlined 24×24. Distintos de `reference_places`.
 */
const KIND_PATHS = {
  entrance: 'M14 6v15H3v-2h2V3h9v1h5v15h2v2h-4V6zm-2 0H7v13h5z',
  security: 'M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11z',
  green: 'M17 12h2L12 2 5.05 12H7l-3.9 6h6.92v4h3.95v-4H21zM6.79 16l3.9-6H8.88l3.13-4.5 3.15 4.5h-1.9l4 6z',
  amenity: 'M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z',
  custom: 'M12 12c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2m6-1.8C18 6.57 15.35 4 12 4s-6 2.57-6 6.2c0 2.34 1.95 5.44 6 9.14 4.05-3.7 6-6.8 6-9.14M12 2c4.2 0 8 3.22 8 8.2 0 3.32-2.67 7.25-8 11.8-5.33-4.55-8-8.48-8-11.8C4 5.22 7.8 2 12 2',
};

function kindPath(kind) {
  return KIND_PATHS[resolveCadProjectPinKind(kind)] || KIND_PATHS[CAD_PROJECT_PIN_KIND_CUSTOM];
}

function renderKindBadgeImageData(kind) {
  const { iconPixelSize: size, iconPixelRatio } = CAD_PROJECT_PIN_STYLE;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 3;

  ctx.clearRect(0, 0, size, size);
  ctx.shadowColor = 'rgba(0,0,0,0.28)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 1;

  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = getCadProjectPinKindColor(kind);
  ctx.fill();

  ctx.shadowColor = 'transparent';
  ctx.lineWidth = 3;
  ctx.strokeStyle = CAD_PROJECT_PIN_STYLE.strokeColor;
  ctx.stroke();

  const iconSize = size * 0.48;
  ctx.save();
  ctx.translate(cx - iconSize / 2, cy - iconSize / 2);
  ctx.scale(iconSize / 24, iconSize / 24);
  ctx.fillStyle = colors.backgroundLight;
  ctx.fill(new Path2D(kindPath(kind)));
  ctx.restore();

  return {
    data: ctx.getImageData(0, 0, size, size),
    pixelRatio: iconPixelRatio,
  };
}

const KIND_IMAGE_IDS = [
  ...CAD_PROJECT_PIN_KIND_IDS.map((kind) => cadProjectPinImageId(kind)),
  cadProjectPinImageId(CAD_PROJECT_PIN_KIND_CUSTOM),
];

/**
 * @param {object} map
 * @returns {boolean}
 */
export function ensureCadProjectPinImages(map) {
  if (!map || typeof map.addImage !== 'function' || typeof document === 'undefined') {
    return false;
  }
  try {
    CAD_PROJECT_PIN_KIND_IDS.forEach((kind) => {
      const id = cadProjectPinImageId(kind);
      if (map.hasImage?.(id)) return;
      const image = renderKindBadgeImageData(kind);
      if (!image) return;
      map.addImage(id, image.data, { pixelRatio: image.pixelRatio });
    });
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * @param {object} map
 * @param {string} imageId
 */
export function ensureCadProjectPinImageForId(map, imageId) {
  if (!map || !imageId || typeof imageId !== 'string') return;
  if (!imageId.startsWith(CAD_PROJECT_PIN_IMAGE_PREFIX)) return;
  if (map.hasImage?.(imageId)) return;
  const kind = imageId.slice(CAD_PROJECT_PIN_IMAGE_PREFIX.length);
  ensureCadProjectPinImages(map);
  if (!KIND_IMAGE_IDS.includes(imageId) && kind) {
    try {
      if (map.hasImage?.(imageId)) return;
      const image = renderKindBadgeImageData(kind);
      if (!image) return;
      map.addImage(imageId, image.data, { pixelRatio: image.pixelRatio });
    } catch (_) { /* estilo a medias */ }
  }
}
